<!-- Base.FileAttach.vue (client-lms) -->
<template>
  <div class="lms_file_attach">
    <LmsBase.FieldInfo>
      최대 10개까지 업로드 가능합니다. (개당 50MB이하)
    </LmsBase.FieldInfo>

    <input ref="fileInput" type="file" multiple hidden @change="onFileChange" />
    <LmsBase.Button color="white" size="36" :disabled="totalCount >= maxFiles" @click="fileInput?.click()" style="align-self: flex-start;">
      <i class="icon_file_up" style="font-size: 16px;"></i>
      파일 첨부
    </LmsBase.Button>

     <div v-if="totalCount > 0" class="lms_file_grid" style="align-self: stretch;">
        <!-- 기존 파일 -->
        <div v-for="(file, idx) in props.existingFiles" :key="'existing-' + idx" class="lms_file_card">
          <button type="button" class="lms_file_card_remove" @click="emit('removeExisting', idx)">
            <i class="icon_x" style="font-size: 14px;"></i>
          </button>
          <div class="lms_file_card_preview">
            <img src="@/assets/common/images/lms/icon_file.svg" alt="" class="lms_file_card_icon_img" />
          </div>
          <div class="lms_file_card_info">
            <span class="lms_file_card_name">{{ file.originalName }}</span>
            <div class="lms_file_card_meta">
              <span class="lms_file_card_ext">{{ file.ext.toUpperCase() }} 파일</span>
              <span class="lms_file_card_sep"> | </span>
              <span class="lms_file_card_size">{{ formatSize(file.volume) }}</span>
            </div>
          </div>
        </div>
        <!-- 새 파일 -->
        <div v-for="(file, idx) in files" :key="'new-' + idx" class="lms_file_card">
          <button type="button" class="lms_file_card_remove" @click="removeFile(idx)">
            <i class="icon_x" style="font-size: 14px;"></i>
          </button>
          <div class="lms_file_card_preview">
            <img src="@/assets/common/images/lms/icon_file.svg" alt="" class="lms_file_card_icon_img" />
          </div>
          <div class="lms_file_card_info">
            <span class="lms_file_card_name">{{ file.name }}</span>
            <div class="lms_file_card_meta">
              <span class="lms_file_card_ext">{{ getFileExt(file.name) }}</span>
              <span class="lms_file_card_sep"> | </span>
              <span class="lms_file_card_size">{{ formatSize(file.size) }}</span>
            </div>
          </div>
        </div>
     </div>

  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed } from "vue";
import { LmsBase } from "@/common/base-components/BaseFactory";

const props = defineProps({
  maxFiles: { type: Number, default: 5 },
  maxSize: { type: Number, default: 50 }, // MB
  existingFiles: { type: Array as () => { originalName: string; ext: string; saveName: string; link: string; volume: number }[], default: () => [] },
});

const emit = defineEmits(["removeExisting"]);

const files = defineModel<File[]>({ default: () => [] });
const previews = ref<(string | null)[]>([]);
const fileInput = ref<HTMLInputElement>();
const totalCount = computed(() => props.existingFiles.length + files.value.length);

const maxSizeBytes = props.maxSize * 1024 * 1024;

const isImage = (file: File) => file.type.startsWith("image/");

const onFileChange = (e: Event) => {
  const input = e.target as HTMLInputElement;
  if (!input.files) return;

  for (const file of Array.from(input.files)) {
    if (totalCount.value >= props.maxFiles) break;
    if (file.size > maxSizeBytes) continue;
    files.value.push(file);

    if (isImage(file)) {
      const reader = new FileReader();
      const idx = files.value.length - 1;
      reader.onload = () => { previews.value[idx] = reader.result as string; };
      reader.readAsDataURL(file);
    } else {
      previews.value.push(null);
    }
  }
  input.value = "";
};

const removeFile = (idx: number) => {
  files.value.splice(idx, 1);
  previews.value.splice(idx, 1);
};

const getFileExt = (name: string): string => {
  const ext = name.split(".").pop()?.toUpperCase() || "";
  return ext ? ext + " 파일" : "파일";
};

const formatSize = (bytes: number): string => {
  if (bytes < 1024) return bytes + "B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + "KB";
  return (bytes / (1024 * 1024)).toFixed(1) + "MB";
};

// 외부에서 v-model로 files가 초기화되면 previews도 리셋
watch(() => files.value.length, (len) => {
  if (len === 0) previews.value = [];
});
</script>
